import type { AliasDto } from "@bafe/api-client";
import { ArrowBendUpRightIcon, PlusIcon } from "@phosphor-icons/react";
import { Button, Card, EmptyState, InlineNotice, PageHeader, Section, StatusPill, useToast } from "@bafe/ui";
import { emailSchema, normalizeDomain, validateLocalPart } from "@bafe/validation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";

import { ConfirmDialog } from "../components/ConfirmDialog";
import { ErrorPanel } from "../components/ErrorPanel";
import { HubSelect } from "../components/HubSelect";
import { LinkButton } from "../components/LinkButton";
import { Popup } from "../components/Popup";
import { api } from "../lib/api";
import { isForwarder } from "../lib/mail-addresses";
import { queryKeys } from "../lib/query";
import { ALIAS_STATUS } from "../lib/status";

export function Forwarders() {
	const [creating, setCreating] = useState(false);
	const [removing, setRemoving] = useState<AliasDto | null>(null);
	const queryClient = useQueryClient();
	const { toast } = useToast();

	const aliases = useQuery({ queryKey: queryKeys.aliases, queryFn: () => api.listAliases() });
	const domains = useQuery({ queryKey: queryKeys.domains, queryFn: () => api.listDomains() });
	const rows = (aliases.data ?? []).filter(isForwarder);
	const hasDomains = (domains.data ?? []).some((domain) => domain.emailEnabled);

	const remove = useMutation({
		mutationFn: (row: AliasDto) => api.deleteAlias(row.id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.aliases });
			setRemoving(null);
			toast({ tone: "success", message: "Forwarder removed." });
		},
	});

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title="Forwarders"
				description="Send mail from an address on your domain to another email."
				action={
					hasDomains ? (
						<Button
							variant="primary"
							onClick={() => setCreating(true)}
							iconLeft={<PlusIcon className="size-4" aria-hidden="true" />}
						>
							Add forwarder
						</Button>
					) : undefined
				}
			/>

			<InlineNotice tone="info" title="How forwarding works">
				A new address such as <span className="text-dns">info@</span> can forward without its own inbox.
				An existing mailbox can forward out too — mail stays in the inbox and a copy goes on. For another
				address on your domain into <span className="text-dns">corporate@</span>, pick that mailbox under
				Send to.
			</InlineNotice>

			{aliases.isPending && (
				<Card>
					<p className="sub">Loading forwarders.</p>
				</Card>
			)}

			{aliases.isError && (
				<ErrorPanel
					error={aliases.error}
					title="We could not load your forwarders."
					onRetry={() => void aliases.refetch()}
					retryPending={aliases.isFetching}
				/>
			)}

			{aliases.isSuccess && (
				<Section>
					{rows.length === 0 ? (
						<Card>
							<EmptyState
								icon={<ArrowBendUpRightIcon className="size-8" aria-hidden="true" />}
								title="No forwarders yet"
								description={
									hasDomains
										? "Add an address such as info@ and send its mail to another email."
										: "Add a domain first, then you can forward addresses on it."
								}
								action={
									hasDomains ? (
										<Button variant="primary" onClick={() => setCreating(true)}>
											Add your first forwarder
										</Button>
									) : (
										<LinkButton to="/manage/domains/new" variant="primary">
											Add a domain
										</LinkButton>
									)
								}
							/>
						</Card>
					) : (
						<Card flush>
							<ul>
								{rows.map((row) => (
									<li
										key={row.id}
										className="flex flex-wrap items-center justify-between gap-3 border-b border-(--border-subtle) px-4 py-3.5 last:border-0"
									>
										<div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
											<span className="text-dns font-medium text-(--text-primary)">
												{row.sourceAddress}
											</span>
											<span aria-hidden="true" className="text-(--text-muted)">
												→
											</span>
											<span className="sr-only">forwards to</span>
											<span className="text-dns text-(--text-secondary)">
												{row.destinationLabel || row.destinationValue}
											</span>
										</div>
										<div className="flex items-center gap-3">
											<StatusPill tone={ALIAS_STATUS[row.status].tone}>
												{ALIAS_STATUS[row.status].label}
											</StatusPill>
											<Button size="sm" variant="ghost" onClick={() => setRemoving(row)}>
												Remove<span className="sr-only"> {row.sourceAddress}</span>
											</Button>
										</div>
									</li>
								))}
							</ul>
						</Card>
					)}
				</Section>
			)}

			{creating && <CreateForwarderDialog onClose={() => setCreating(false)} />}

			<ConfirmDialog
				open={removing !== null}
				onClose={() => setRemoving(null)}
				title="Remove this forwarder?"
				description={
					removing
						? `Mail sent to ${removing.sourceAddress} will stop going to ${removing.destinationLabel || removing.destinationValue}.`
						: undefined
				}
				confirmLabel="Remove forwarder"
				danger
				pending={remove.isPending}
				error={remove.error}
				onConfirm={() => {
					if (removing) remove.mutate(removing);
				}}
			/>
		</div>
	);
}

function CreateForwarderDialog({ onClose }: { onClose: () => void }) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const domains = useQuery({ queryKey: queryKeys.domains, queryFn: () => api.listDomains() });
	const domainList = (domains.data ?? []).filter((domain) => domain.emailEnabled);
	const [domainId, setDomainId] = useState(() => domainList[0]?.id ?? "");
	const [localPart, setLocalPart] = useState("");
	const [destination, setDestination] = useState("");

	const effectiveDomainId = domainId || (domainList[0]?.id ?? "");
	const domain = domainList.find((item) => item.id === effectiveDomainId) ?? null;
	const local = validateLocalPart(localPart);
	const dest = emailSchema.safeParse(destination.trim());
	const deliversToMailboxOnDomain =
		dest.success &&
		domain !== null &&
		dest.data.endsWith(`@${normalizeDomain(domain.domainName)}`);
	const ready = local.ok && dest.success && effectiveDomainId !== "";

	const create = useMutation({
		mutationFn: () =>
			api.createAlias({
				domainId: effectiveDomainId,
				localPart: local.normalized,
				destinationType: "external",
				mailboxId: null,
				externalAddress: dest.success ? dest.data : destination.trim(),
			}),
		onSuccess: (row) => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.aliases });
			const verb = row.destinationType === "mailbox" ? "delivers to" : "forwards to";
			toast({ tone: "success", message: `${row.sourceAddress} now ${verb} ${row.destinationValue}.` });
			onClose();
		},
	});

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!ready || create.isPending) return;
		create.mutate();
	}

	return (
		<Popup open title="Add forwarder" onClose={onClose}>
			<form onSubmit={submit} noValidate>
				<p className="sub">
					Mail to this address goes on to another email. Use a mailbox address here to keep a copy in
					webmail.
				</p>
				{create.isError && (
					<ErrorPanel error={create.error} title="We could not add this forwarder." />
				)}
				<label htmlFor="forwarder-local">New address</label>
				<div className="row tight">
					<input
						id="forwarder-local"
						value={localPart}
						autoComplete="off"
						autoCapitalize="none"
						spellCheck={false}
						placeholder="info"
						autoFocus
						onChange={(event) => setLocalPart(event.target.value)}
					/>
					<HubSelect
						id="forwarder-domain"
						label="Domain"
						value={effectiveDomainId}
						items={domainList.map((item) => ({
							value: item.id,
							label: `@${item.domainName}`,
						}))}
						onChange={setDomainId}
						className="field-select-inline"
					/>
				</div>
				{localPart.length > 0 && !local.ok && local.message && <p className="err">{local.message}</p>}
				{local.ok && domain && (
					<p className="sub">
						<span className="text-dns">
							{local.normalized}@{domain.domainName}
						</span>{" "}
						will send mail on.
					</p>
				)}
				<label htmlFor="forwarder-dest">Send to</label>
				<input
					id="forwarder-dest"
					type="email"
					value={destination}
					autoComplete="off"
					autoCapitalize="none"
					spellCheck={false}
					placeholder="name@example.com"
					onChange={(event) => setDestination(event.target.value)}
				/>
				{destination.trim().length > 0 && !dest.success && (
					<p className="err">Enter the full email address that should receive the mail.</p>
				)}
				{deliversToMailboxOnDomain && (
					<InlineNotice tone="info" title="Delivers into a mailbox here">
						Mail to this address lands in {destination.trim()}. Nothing is stored under the new address
						and nobody signs into it.
					</InlineNotice>
				)}
				{dest.success && !deliversToMailboxOnDomain && (
					<InlineNotice tone="caution" title="Delivery after it leaves">
						Key cannot see what happens once the message reaches another provider. Use a mailbox on this
						workspace when you need to keep a copy.
					</InlineNotice>
				)}
				<p className="actions">
					<button className="btn" type="submit" disabled={!ready || create.isPending}>
						{create.isPending ? "Adding" : "Add forwarder"}
					</button>
					<button className="btn ghost" type="button" onClick={onClose}>
						Cancel
					</button>
				</p>
			</form>
		</Popup>
	);
}
